import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { ProductStatePill } from "@/components/cinch/ProductStatePill";
import { TierChip } from "@/components/cinch/TierChip";
import { AccountDetailContent } from "@/features/renewals/components/AccountDetailContent";
import { fmtCur } from "@/features/renewals/format";

import type { UpsellOpsSearch } from "..";
import type { WhitespaceCustomer, WhitespaceGroup } from "../api";
import { pillColor } from "../lib";

/**
 * Account drawer for the whitespace table, bound to /upsell-ops:
 *
 *   ?account=<qbId>              → quick view (the account's upsell opportunity)
 *   ?account=<qbId>&expanded=1   → expanded (full account: billing info + history)
 *
 * The quick view is upsell-focused — owned products with their billed/contract
 * state vs the open whitespace list. Expanded reuses the shared renewals
 * account view (now with the subscription-vs-usage billing timeline).
 */
export function UpsellAccountDrawer({
  customers,
  groups,
}: {
  customers: WhitespaceCustomer[];
  groups: WhitespaceGroup[];
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/upsell-ops" }) as UpsellOpsSearch;
  const qbId = search.account ?? null;
  const expanded = !!search.expanded;
  const customer = qbId != null ? customers.find((c) => c.qb_id === qbId) ?? null : null;

  const go = (next: Partial<UpsellOpsSearch>) =>
    navigate({
      to: "/upsell-ops",
      search: (prev: UpsellOpsSearch) => ({ ...prev, ...next }),
    });

  return (
    <EntityDrawer
      open={qbId != null}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ account: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (qbId != null) go({ expanded: exp ? true : undefined });
      }}
      onClose={() => {
        if (qbId == null) return;
        if (expanded) go({ expanded: undefined });
        else go({ account: undefined, expanded: undefined });
      }}
      title={customer?.name ?? "Account"}
      subtitle={qbId != null ? `QB-${qbId}` : ""}
    >
      {qbId != null &&
        (expanded ? (
          <AccountDetailContent qbId={qbId} />
        ) : (
          <QuickOpportunity customer={customer} groups={groups} />
        ))}
    </EntityDrawer>
  );
}

function QuickOpportunity({
  customer,
  groups,
}: {
  customer: WhitespaceCustomer | null;
  groups: WhitespaceGroup[];
}) {
  if (!customer) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        This account isn't in the current whitespace dataset (check the legacy
        toggle or the billing month).
      </p>
    );
  }
  const owned = groups.filter((g) => customer.products[g.key] !== "none");
  const open = groups.filter((g) => customer.products[g.key] === "none");
  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <TierChip tier={customer.tier} />
        <span className="text-sm font-semibold tabular-nums">{fmtCur(customer.arr)} ARR</span>
        {customer.since_year && (
          <span className="text-xs text-muted-foreground">
            Customer since {customer.since_year}
          </span>
        )}
        {!customer.platform && (
          <span className="rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning">
            legacy
          </span>
        )}
      </div>

      <div className="text-sm">
        <span className="text-muted-foreground">CSM: </span>
        <span className="font-medium">{customer.csm_label ?? "Unassigned"}</span>
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Open upsells ({open.length})
        </h3>
        {open.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Fully attached — every tracked product is on this account. 🎉
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {open.map((g) => (
              <li key={g.key} className="flex items-center gap-2 text-sm">
                <ProductStatePill state="none" color={pillColor(g.color)} />
                {g.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Already attached ({owned.length})
        </h3>
        {owned.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing attached yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {owned.map((g) => (
              <li key={g.key} className="flex items-center gap-2 text-sm">
                <ProductStatePill
                  state={customer.products[g.key] ?? "none"}
                  color={pillColor(g.color)}
                />
                {g.label}
                <span className="text-xs text-muted-foreground">
                  {customer.products[g.key] === "billed" ? "billing" : "on contract, not billing"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Expand the drawer (top-right) for the full account view — contracts,
        companies, and the monthly subscription vs usage billing timeline.
      </p>
    </div>
  );
}
